import React, { useCallback, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Row, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateMomentFormat } from 'v2/formatters/date';
import { ETicketStatus } from 'v2/interfaces/help';
import type { ITicket } from 'v2/interfaces/help';
import helpService from 'v2/services/help';

interface MyTicketsProps {
  handleHistoryClick: (ticketId: number) => void;
}

const MyTickets = ({ handleHistoryClick }: MyTicketsProps) => {
  useObservable(() => helpService.loadTickets(), []);
  const [isLoading] = useObservable(() => helpService.getLoadingTickets(), []);
  const [data] = useObservable(() => helpService.getTickets(), []);

  const handleTicketHistoryClick = useCallback(
    (tickedId: number) => handleHistoryClick(tickedId),
    [handleHistoryClick]
  );

  const columns: ColumnsType<ITicket> = useMemo(
    () => [
      {
        title: 'Ticket',
        dataIndex: 'id',
        key: 'id',
        width: 100
      },
      {
        title: 'Título',
        dataIndex: 'title',
        key: 'title',
        render: (title: string) => (
          <Truncate count={50} final='...'>
            {title}
          </Truncate>
        )
      },
      {
        title: 'Criação',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (value: string) => dateMomentFormat(value, 'DD/MM/YYYY HH:mm')
      },
      {
        title: 'Última atualização',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        render: (value: string) => dateMomentFormat(value, 'DD/MM/YYYY HH:mm')
      },
      {
        title: 'Data de solução',
        dataIndex: 'solved_at',
        key: 'solved_at',
        width: 160,
        render: (value: string | null) => (value ? dateMomentFormat(value, 'DD/MM/YYYY HH:mm') : '-')
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: number) => ETicketStatus[status]
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, ticket) => (
          <Tooltip title='Visualizar histórico' placement='top'>
            <Button
              shape='circle'
              type='text'
              onClick={() => handleTicketHistoryClick(ticket.id)}
              icon={<ClockCircleOutlined className='text-xl' />}
            />
          </Tooltip>
        )
      }
    ],
    [handleTicketHistoryClick]
  );

  return (
    <>
      <Row>
        <Col xs={12} md={16}>
          <h5 className='text-xl font-semibold'>Meus tickets</h5>
        </Col>

        <Col xs={12} md={8}>
          <div className='text-right'>
            <PermissionHide action='myeduzz:ajuda:abrir-chamado'>
              <a href='https://suporte.eduzz.com/chamado-produto/myeduzz' target='_blank' rel='noreferrer'>
                <Button type='primary' icon={<PlusOutlined />}>
                  Novo Ticket
                </Button>
              </a>
            </PermissionHide>
          </div>
        </Col>
      </Row>

      <div className='mt-4' />
      <div className='w-full overflow-x-auto rounded-sm bg-white shadow-sm'>
        <Table<ITicket>
          rowKey='id'
          columns={columns}
          dataSource={data || []}
          loading={!!isLoading}
          scroll={{ x: 900 }}
          pagination={false}
          locale={{ emptyText: 'Nenhum registro encontrado' }}
        />
      </div>
    </>
  );
};

export default MyTickets;
